import React from 'react';
import { Star, Video, AlertTriangle, Siren, CheckCircle2 } from 'lucide-react';
import { fmtInt, fmtGifts, fmtUSD, fmtHours } from './starFormat';
import { StarProgressBar, MetaTile, ObsidianCard, GRADE_GRADIENT } from './StarBits';

export const HostStarHero = ({ grade, status, cycle, maintenance }) => {
  const m = maintenance;
  const eq = m.equivalences;
  return (
    <ObsidianCard testId="host-star-hero-card" className="space-y-5">
      {/* 1 · identidade */}
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="host-star-level-badge"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-display text-xs font-black tracking-wide text-black shadow-[0_4px_20px_rgba(245,158,11,0.35)] sm:text-sm"
          style={{ background: GRADE_GRADIENT[grade] || GRADE_GRADIENT.A }}>
          <Star className="h-3.5 w-3.5" strokeWidth={3} /> STAR HOST {grade}
        </span>
        <span data-testid="host-star-status-badge"
          className={`rounded-full border px-2.5 py-1 text-[11px] font-bold backdrop-blur-md ${status === 'active'
            ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
            : 'border-amber-400/30 bg-amber-400/10 text-amber-300'}`}>
          {status === 'active' ? '● Ativo' : '⏸ Pausado'}
        </span>
        <span data-testid="host-star-cycle-period"
          className="ml-auto rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-mono text-[11px] text-zinc-400">
          {cycle.start} → {cycle.end}
        </span>
      </div>

      {/* 2 · o número que importa */}
      <div>
        {m.achieved ? (
          <div data-testid="host-star-remaining-crystals" className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-7 w-7 shrink-0 text-emerald-400" strokeWidth={2.5} />
            <div>
              <p className="font-display text-2xl font-black leading-none tracking-tight text-emerald-300 sm:text-3xl">
                META DO CICLO CUMPRIDA
              </p>
              <p className="mt-1.5 text-xs text-zinc-400 sm:text-sm">
                Nível {grade} garantido nos cristais · mantenha os {m.min_valid_days} dias válidos
              </p>
            </div>
          </div>
        ) : (
          <div data-testid="host-star-remaining-crystals">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-400/80">Faltam</p>
            <p className="font-display text-3xl font-black leading-none tracking-tight text-white sm:text-4xl">
              <span className="font-mono">{fmtInt(m.remaining)}</span>
              <span className="ml-2 align-middle font-display text-base font-bold text-amber-300 sm:text-lg">cristais</span>
            </p>
            <p className="mt-1.5 text-xs text-zinc-400 sm:text-sm">
              de comissão para manter o nível {grade}
              {m.days_left !== null && m.days_left !== undefined &&
                ` · ${m.days_left === 0 ? 'último dia' : `${m.days_left} dia(s) de ciclo`}`}
            </p>
          </div>
        )}
      </div>

      {/* 3 · progresso */}
      <div className="space-y-2">
        <StarProgressBar pct={m.pct} achieved={m.achieved} testId="host-star-progress-bar" />
        <div className="flex items-center justify-between font-mono text-[11px] text-zinc-400">
          <span>{fmtInt(m.current)} de {fmtInt(m.target)} cristais</span>
          <span className={`font-bold ${m.achieved ? 'text-emerald-300' : 'text-amber-300'}`}>{m.pct}%</span>
        </div>
      </div>

      {/* 4 · tradução em presentes */}
      {!m.achieved && (
        <div data-testid="host-star-equivalence-line"
          className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-3 backdrop-blur-md">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-amber-400/80">O que você precisa receber</p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-200 sm:text-sm">
            <span className="font-mono font-bold text-amber-200" data-testid="host-star-equiv-exclusive">
              {fmtGifts(eq.exclusive_gifts)}
            </span> presentes exclusivos
            <span className="mx-1.5 text-zinc-500">ou</span>
            <span className="font-mono font-bold text-emerald-300" data-testid="host-star-equiv-luck">
              {fmtGifts(eq.luck_gifts)}
            </span> presentes da sorte
            <span className="ml-1.5 font-mono font-bold text-white" data-testid="host-star-equiv-usd">
              ({fmtUSD(eq.usd)})
            </span>
          </p>
        </div>
      )}

      {/* 5 · apoio */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <MetaTile testId="host-star-support-valid-days" label="Dias válidos no ciclo"
          tone={m.min_days_met ? 'emerald' : 'rose'}
          value={`${fmtInt(m.valid_days)} de ${m.min_valid_days}`}
          sub={m.min_days_met ? 'mínimo cumprido' : 'mínimo ainda não cumprido'} />
        <MetaTile testId="host-star-support-avg-daily" label="Meta média por dia"
          tone="white" value={fmtInt(m.daily_avg_required)}
          sub={`× ${m.days_counted_for_target} dia(s) = ${fmtInt(m.target)}`} />
        <MetaTile testId="host-star-support-daily-hours" label="Regra diária"
          value={`${fmtHours(m.min_video_minutes)} de vídeo`}
          sub={m.exemption_applied
            ? `isenção de 1h sobre ${fmtHours(m.base_min_video_minutes)} · áudio não conta`
            : `1h menos com isenção · áudio não conta`} />
      </div>

      <p className="flex items-start gap-1.5 text-[10px] leading-relaxed text-zinc-500">
        <Video className="mt-0.5 h-3 w-3 shrink-0 text-zinc-600" />
        Os dias válidos vêm direto do Star Host da KAKO, que já considera a sua hora mínima e a
        isenção de 1 hora de quem se destacou no ciclo anterior. Live de áudio nunca conta.
      </p>
    </ObsidianCard>
  );
};

export const HostStarAlerts = ({ alerts }) => (
  !alerts?.length ? null : (
    <div className="space-y-2">
      {alerts.map((a, i) => (
        <div key={i} data-testid={`host-risk-alert-${a.level}`}
          style={{ background: 'linear-gradient(145deg,#0D111A 0%,#161D2C 100%)' }}
          className={`flex items-start gap-2.5 rounded-2xl border p-3 text-xs font-medium sm:text-sm ${
            a.level === 'danger'
              ? 'border-rose-500/50 text-rose-100 shadow-[0_0_28px_-10px_rgba(244,63,94,0.7)]'
              : 'border-amber-500/50 text-amber-100 shadow-[0_0_28px_-12px_rgba(245,158,11,0.6)]'}`}>
          {a.level === 'danger'
            ? <Siren className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
            : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />}
          <span>{a.message}</span>
        </div>
      ))}
    </div>
  )
);
